import { QuillAgent, } from "@quill/core";
import { icons } from "./icons.js";
import { styles } from "./styles.js";
import { affirmative, WebSpeechSTT, WebSpeechTTS } from "./voice.js";
export function mountQuill(opts) {
    const host = document.createElement("div");
    host.setAttribute("data-quill-root", "");
    const shadow = host.attachShadow({ mode: "open" });
    document.body.appendChild(host);
    const styleEl = document.createElement("style");
    styleEl.textContent = styles;
    shadow.appendChild(styleEl);
    const rootHost = shadow;
    // Theme + accent live on :host — set via the host element.
    host.setAttribute("data-theme", opts.theme ?? "auto");
    if (opts.accent)
        host.style.setProperty("--q-accent", opts.accent);
    // Mirror onto shadow host for :host selector matching.
    shadow.host.setAttribute("data-theme", opts.theme ?? "auto");
    const pos = opts.position ?? "bottom-right";
    const cornerClass = pos === "bottom-left" ? "corner-bl" : "corner-br";
    const stt = opts.voice ? new WebSpeechSTT() : null;
    const tts = opts.voice ? new WebSpeechTTS() : null;
    const voiceOn = Boolean(opts.voice && stt?.available);
    // ── Launcher ──────────────────────────────────────────────────────────
    const launcher = document.createElement("button");
    launcher.className = `launcher ${cornerClass}`;
    launcher.setAttribute("aria-label", "Open Quill assistant");
    launcher.innerHTML = icons.quill;
    shadow.appendChild(launcher);
    // ── Panel ─────────────────────────────────────────────────────────────
    const panel = document.createElement("div");
    panel.className = `panel ${pos === "bottom-left" ? "bl" : ""}`;
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-label", "Quill assistant");
    panel.style.display = "none";
    panel.style[pos === "bottom-left" ? "left" : "right"] = "20px";
    panel.style.bottom = "20px";
    panel.innerHTML = `
    <div class="header">
      <span class="mark">${icons.quill}</span>
      <span class="title">Quill</span>
      <span class="status"><span class="dot idle"></span><span class="status-text">Ready</span></span>
      <button class="close" aria-label="Close">${icons.close}</button>
    </div>
    <div class="transcript" role="log" aria-live="polite"></div>
    <details class="feed"><summary>Activity</summary><div class="feed-items"></div></details>
    <form class="composer">
      <textarea rows="1" placeholder="Ask Quill to do something…" aria-label="Message Quill"></textarea>
      ${voiceOn ? `<button type="button" class="icon-btn mic" aria-label="Voice input">${icons.mic}</button>` : ""}
      <button type="submit" class="icon-btn send" aria-label="Send">${icons.send}</button>
    </form>
  `;
    shadow.appendChild(panel);
    const q = (sel) => panel.querySelector(sel);
    const transcript = q(".transcript");
    const feedItems = q(".feed-items");
    const textarea = q("textarea");
    const composer = q(".composer");
    const sendBtn = q(".send");
    const micBtn = voiceOn ? q(".mic") : null;
    const statusDot = q(".status .dot");
    const statusText = q(".status-text");
    let currentAgent = null;
    let pending = null;
    let listening = false;
    const highlights = new Map();
    showEmptyState();
    function showEmptyState() {
        transcript.innerHTML = `<div class="empty"><div class="big">Hi, I'm Quill</div>${opts.greeting ?? "Tell me what to do on this page and I'll do it — I'll ask before anything sensitive."}</div>`;
    }
    function clearEmptyState() {
        const e = transcript.querySelector(".empty");
        if (e)
            transcript.innerHTML = "";
    }
    function addMessage(role, text) {
        clearEmptyState();
        const el = document.createElement("div");
        el.className = `msg ${role}`;
        el.textContent = text;
        transcript.appendChild(el);
        transcript.scrollTop = transcript.scrollHeight;
        if (role === "assistant" && tts?.available && voiceMode)
            tts.speak(text);
    }
    function addToolLine(summary, ref) {
        clearEmptyState();
        const el = document.createElement("div");
        el.className = "tool-line";
        el.innerHTML = `↳ <span></span>${ref ? ` <code>${ref}</code>` : ""}`;
        el.querySelector("span").textContent = summary;
        transcript.appendChild(el);
        transcript.scrollTop = transcript.scrollHeight;
    }
    function addFeedItem(summary, ok) {
        const el = document.createElement("div");
        el.className = `item ${ok ? "" : "err"}`;
        el.innerHTML = `<span class="sd"></span><span></span>`;
        el.querySelector("span:last-child").textContent = summary;
        feedItems.appendChild(el);
        feedItems.scrollTop = feedItems.scrollHeight;
    }
    function setStatus(kind, text) {
        statusDot.className = `dot ${kind === "stopped" ? "error" : kind}`;
        statusText.textContent = text;
    }
    // ── On-page highlight overlays (appended to the host page, not the shadow) ─
    function highlight(ref, kind) {
        const el = currentAgent?.registry.resolve(ref);
        if (!el)
            return;
        const rect = el.getBoundingClientRect();
        let box = highlights.get(ref);
        if (!box) {
            box = document.createElement("div");
            box.className = "q-highlight";
            // Nib-stroke ring (see /design.md): accent ring + white inner keyline +
            // soft halo — a construction that survives light AND dark host pages.
            Object.assign(box.style, {
                position: "fixed",
                zIndex: "2147482000",
                pointerEvents: "none",
                border: `2px solid ${opts.accent ?? "#0F76B8"}`,
                background: "rgba(15,118,184,.10)",
                borderRadius: "8px",
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,.55), 0 0 0 4px rgba(15,118,184,.14)",
                transition: "opacity .26s ease",
            });
            document.body.appendChild(box);
            highlights.set(ref, box);
        }
        Object.assign(box.style, {
            left: `${rect.left - 3}px`,
            top: `${rect.top - 3}px`,
            width: `${rect.width + 6}px`,
            height: `${rect.height + 6}px`,
            opacity: "1",
        });
        const hold = kind === "act" ? 900 : 600;
        setTimeout(() => {
            if (box)
                box.style.opacity = "0";
            setTimeout(() => {
                box?.remove();
                highlights.delete(ref);
            }, 260);
        }, hold);
    }
    // ── Confirmation / question cards (the ConfirmFn) ─────────────────────
    const confirm = (req) => new Promise((resolve) => {
        clearEmptyState();
        const card = document.createElement("div");
        card.className = `confirm ${req.danger ? "danger" : ""}`;
        card.setAttribute("role", "alertdialog");
        const isConfirm = req.kind === "confirm";
        card.innerHTML = `
        <div class="q"></div>
        <div class="row">
          <button class="btn ghost deny">${isConfirm ? "Deny" : "Cancel"}</button>
          <button class="btn primary allow" ${isConfirm ? "" : "disabled"}>${isConfirm ? "Allow" : "Reply below"}</button>
        </div>`;
        card.querySelector(".q").textContent = req.question;
        transcript.appendChild(card);
        transcript.scrollTop = transcript.scrollHeight;
        const denyBtn = card.querySelector(".deny");
        const allowBtn = card.querySelector(".allow");
        const finish = (answer) => {
            pending = null;
            textarea.placeholder = "Ask Quill to do something…";
            card.querySelectorAll("button").forEach((b) => (b.disabled = true));
            allowBtn.classList.toggle("selected", /^\s*(y|allow|ok)/i.test(answer));
            resolve(answer);
        };
        // Safe default: focus lands on Deny; Enter is not bound to Allow.
        denyBtn.addEventListener("click", () => finish("no"));
        allowBtn.addEventListener("click", () => finish("yes"));
        denyBtn.focus();
        pending = { kind: req.kind, resolve: finish };
        if (!isConfirm) {
            textarea.placeholder = "Reply to Quill…";
            textarea.focus();
        }
        if (voiceMode && tts?.available) {
            tts.speak(isConfirm ? `${req.question} Say yes to allow, or no to deny.` : req.question);
        }
    });
    // ── Voice mode ────────────────────────────────────────────────────────
    let voiceMode = false;
    function toggleListening() {
        if (!stt?.available)
            return;
        listening = !listening;
        micBtn?.classList.toggle("listening", listening);
        if (listening) {
            voiceMode = true;
            textarea.placeholder = "Listening…";
            stt.start((final) => {
                listening = false;
                micBtn?.classList.remove("listening");
                textarea.placeholder = "Ask Quill to do something…";
                // If a confirmation is pending, answer it by voice; else run as a task.
                if (pending)
                    pending.resolve(pending.kind === "confirm" ? (affirmative(final) ? "yes" : "no") : final);
                else
                    void run(final);
            }, (interim) => {
                textarea.value = interim;
            });
        }
        else {
            stt.stop();
            textarea.placeholder = "Ask Quill to do something…";
        }
    }
    micBtn?.addEventListener("click", toggleListening);
    // ── Activity sink ─────────────────────────────────────────────────────
    const onActivity = (e) => {
        switch (e.type) {
            case "assistant_text":
                addMessage("assistant", e.text);
                break;
            case "tool_result":
                if (e.name !== "read_page" && e.name !== "query" && e.name !== "read_element")
                    addToolLine(e.summary);
                addFeedItem(e.summary, e.ok);
                break;
            case "highlight":
                highlight(e.ref, e.kind);
                break;
            case "status":
                if (e.status === "working")
                    setStatus("working", "Working…");
                else if (e.status === "idle")
                    setStatus("idle", "Done");
                else if (e.status === "stopped")
                    setStatus("stopped", "Stopped");
                else if (e.status === "error") {
                    setStatus("error", "Error");
                    addMessage("assistant", "I couldn't complete that task. Please try again.");
                }
                break;
        }
    };
    // ── Run a task ────────────────────────────────────────────────────────
    let running = false;
    async function run(task) {
        if (running || !task.trim())
            return;
        running = true;
        addMessage("user", task);
        setSendToStop(true);
        const agent = new QuillAgent({
            root: opts.root ?? (() => document.body),
            policy: opts.policy,
            confirm,
            onActivity,
            planner: opts.planner,
        });
        currentAgent = agent;
        try {
            await agent.run(task);
        }
        finally {
            running = false;
            currentAgent = null;
            setSendToStop(false);
        }
    }
    function setSendToStop(isRunning) {
        if (isRunning) {
            sendBtn.classList.replace("send", "stop");
            sendBtn.innerHTML = icons.stop;
            sendBtn.setAttribute("aria-label", "Stop");
        }
        else {
            sendBtn.classList.replace("stop", "send");
            sendBtn.innerHTML = icons.send;
            sendBtn.setAttribute("aria-label", "Send");
        }
    }
    composer.addEventListener("submit", (e) => {
        e.preventDefault();
        const task = textarea.value.trim();
        if (pending && task) {
            textarea.value = "";
            addMessage("user", task);
            pending.resolve(pending.kind === "confirm" ? (affirmative(task) ? "yes" : "no") : task);
            return;
        }
        if (running) {
            currentAgent?.stop();
            return;
        }
        textarea.value = "";
        void run(task);
    });
    textarea.addEventListener("input", () => {
        textarea.style.height = "auto";
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
    });
    textarea.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            composer.requestSubmit();
        }
    });
    // ── Open / close ──────────────────────────────────────────────────────
    function open() {
        panel.style.display = "flex";
        launcher.style.display = "none";
        textarea.focus();
    }
    function close() {
        panel.style.display = "none";
        launcher.style.display = "grid";
        launcher.focus();
    }
    launcher.addEventListener("click", open);
    q(".close").addEventListener("click", close);
    if (opts.open)
        open();
    else
        launcher.style.display = "grid";
    return {
        open,
        close,
        run,
        destroy() {
            highlights.forEach((b) => b.remove());
            host.remove();
        },
    };
}
//# sourceMappingURL=panel.js.map